import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client, { formatApiError } from "@/lib/api";
import { Lightbulb, Heart, Star, Send, Sparkles, BookOpen, ArrowLeft } from "lucide-react";

const KINDS = [
  { v: "story", l: "A story idea" },
  { v: "moral", l: "A moral or lesson" },
  { v: "other", l: "Something else" },
];

export default function Community() {
  const [ideas, setIdeas] = useState(null);
  const [spotlight, setSpotlight] = useState(null);
  const [text, setText] = useState("");
  const [kind, setKind] = useState("story");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [i, s] = await Promise.all([
      client.get("/community/ideas"),
      client.get("/community/spotlight"),
    ]);
    setIdeas(i.data.ideas);
    setSpotlight(s.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const t = text.trim();
    if (t.length < 4 || busy) return;
    setBusy(true); setErr("");
    try {
      await client.post("/community/ideas", { text: t, kind });
      setText("");
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const vote = async (id) => {
    // Optimistic — a child tapping a heart should never wait for a round trip.
    setIdeas((list) => list.map((d) => d.id === id
      ? { ...d, voted: !d.voted, vote_count: d.vote_count + (d.voted ? -1 : 1) }
      : d));
    try { await client.post(`/community/ideas/${id}/vote`); } catch { load(); }
  };

  return (
    <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="community-page">
      <Link to="/dashboard" data-testid="back-to-dashboard" className="flex w-fit items-center gap-1.5 text-hv-teal font-bold text-sm mb-5">
        <ArrowLeft size={16} strokeWidth={2.5} /> Back to dashboard
      </Link>
      <span className="hv-badge">Community</span>
      <h1 className="hv-heading mt-4 text-3xl sm:text-4xl">
        <span className="text-hv-navy">Your ideas make the</span> <span className="text-hv-coral">next story.</span>
      </h1>
      <p className="mt-3 text-hv-slate leading-relaxed max-w-2xl">
        Tell us a story you wish existed, or a lesson you would love your class to learn.
        Everyone can read and vote — students, parents and teachers alike.
      </p>

      {/* Spotlight — a reason to look in on a quiet day */}
      {spotlight?.featured_story && (
        <div className="mt-8 hv-card p-7 sm:p-9" data-testid="spotlight">
          <div className="flex items-center gap-3">
            <div className="hv-icon-circle-gold w-11 h-11 rounded-full flex items-center justify-center">
              <Star size={20} strokeWidth={2.5} />
            </div>
            <span className="hv-badge">This month&apos;s spotlight</span>
          </div>
          <h2 className="hv-heading mt-4 text-2xl text-hv-navy">{spotlight.featured_story.title}</h2>
          <p className="mt-1 text-hv-slate text-sm">
            Story #{spotlight.featured_story.global_sequence} · Juz {spotlight.featured_story.juz_number}
          </p>
          <Link to={`/story/${spotlight.featured_story.global_sequence}`} data-testid="spotlight-open"
                className="hv-btn-secondary mt-5 inline-flex items-center gap-2">
            <BookOpen size={18} strokeWidth={2.5} /> Read the featured story
          </Link>
        </div>
      )}

      {/* Idea Box */}
      <div className="mt-6 hv-card p-7" data-testid="idea-box">
        <div className="flex items-center gap-3">
          <div className="hv-icon-circle-coral w-11 h-11 rounded-full flex items-center justify-center">
            <Lightbulb size={20} strokeWidth={2.5} />
          </div>
          <span className="hv-badge hv-badge-coral">Story Idea Box</span>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {KINDS.map((k) => (
            <button key={k.v} type="button" onClick={() => setKind(k.v)} data-testid={`idea-kind-${k.v}`}
                    className={`rounded-full px-4 min-h-[44px] text-sm font-bold border-2 transition ${
                      kind === k.v ? "bg-hv-navy text-white border-hv-navy" : "bg-white text-hv-navy border-[#E5DFC8]"}`}>
              {k.l}
            </button>
          ))}
        </div>

        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={500}
                  data-testid="idea-text"
                  placeholder="I wish there was a story about…"
                  className="mt-4 w-full rounded-2xl border-2 border-[#E5DFC8] px-4 py-3 bg-white leading-relaxed" />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button onClick={submit} disabled={busy || text.trim().length < 4} data-testid="idea-submit"
                  className="hv-btn-primary inline-flex items-center gap-2 disabled:opacity-50">
            <Send size={18} strokeWidth={2.5} /> {busy ? "Sending…" : "Share my idea"}
          </button>
          <span className="text-hv-slate text-xs">{text.length}/500</span>
        </div>
        {err && <div className="mt-3 text-sm font-bold text-hv-coral" data-testid="idea-error">{err}</div>}
      </div>

      {/* The board */}
      <div className="mt-8">
        <h2 className="hv-heading text-2xl text-hv-navy">What everyone is asking for</h2>
        <div className="mt-4 space-y-3">
          {(ideas || []).map((d) => (
            <div key={d.id} className="hv-card p-5 flex items-start gap-4" data-testid={`idea-${d.id}`}>
              <button onClick={() => vote(d.id)} data-testid={`idea-vote-${d.id}`}
                      className={`w-14 min-h-[44px] rounded-2xl border-2 flex flex-col items-center justify-center shrink-0 transition ${
                        d.voted ? "border-hv-coral bg-[#FFF3F0]" : "border-[#E5DFC8] bg-white hover:border-hv-coral"}`}>
                <Heart size={17} strokeWidth={2.5} className="text-hv-coral" fill={d.voted ? "#D2806D" : "none"} />
                <span className="text-xs font-bold text-hv-navy mt-0.5">{d.vote_count}</span>
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-hv-navy leading-relaxed">{d.text}</p>
                <div className="mt-2 flex items-center gap-2 text-xs text-hv-slate">
                  <Sparkles size={13} className="text-hv-teal" strokeWidth={2.5} />
                  <span className="font-bold">{d.author_name}</span>
                  <span>·</span>
                  <span className="capitalize">{d.author_role}</span>
                </div>
              </div>
            </div>
          ))}
          {ideas && !ideas.length && (
            <div className="hv-card p-6 text-hv-slate" data-testid="no-ideas">
              No ideas yet — be the first to suggest one.
            </div>
          )}
          {!ideas && <div className="p-6 text-hv-slate">Loading…</div>}
        </div>
      </div>
    </main>
  );
}
